import { useMutation, useQuery } from "@apollo/client";
import ArrowBack from "@mui/icons-material/ArrowBack";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import CheckBox from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlank from "@mui/icons-material/CheckBoxOutlineBlank";
import DomainAdd from "@mui/icons-material/DomainAdd";
import EditNote from "@mui/icons-material/EditNote";
import Label from "@mui/icons-material/Label";
import PublishIcon from "@mui/icons-material/Publish";
import Replay from "@mui/icons-material/Replay";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridRowId,
} from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { mdHeight as appBarMdHeight, xsHeight as appBarXsHeight } from "components/AppBar";
import { ConfirmDialog } from "components/ConfirmDialog";
import { DataGrid } from "components/DataGrid";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { S3FileLink } from "components/S3FileLink";
import CopyableTypography from "components/Typography/CopyableTypography";
import {
  PipelineRejectionType,
  WastePipelineRunMetadataQuery,
  WastePipelineRunRowsQuery,
  WastePipelineRunRowsQueryVariables,
  WastePipelineRunRejectedRowsQuery,
  WastePipelineRunRejectedRowGroupsQuery,
  WastePipelineRunRejectedRowGroupsQueryVariables,
  WastePipelineRunRejectedRowsByReasonQuery,
  WastePipelineRunRejectedRowsByReasonQueryVariables,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { inProgressJobStatuses } from "hooks/useLongPollingForJob";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { usePaginationModel } from "hooks/usePaginationModel";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { prettyPrintDateTime } from "util/dates";

import {
  INGEST_WASTE_PIPELINE_DATA_MUTATION,
  MARK_FACILITY_NOT_FOUND_ROWS_RESOLVED_MUTATION,
  REPROCESS_RESOLVED_PIPELINE_ROWS_MUTATION,
  UNMARK_FACILITY_NOT_FOUND_ROWS_RESOLVED_MUTATION,
  UPDATE_PIPELINE_OUTPUT_DATA_MUTATION,
  WASTE_PIPELINE_RUN_METADATA_QUERY,
  WASTE_PIPELINE_RUN_ROWS_QUERY,
  WASTE_PIPELINE_RUN_REJECTED_ROW_GROUPS_QUERY,
  WASTE_PIPELINE_RUN_REJECTED_ROWS_BY_REASON_QUERY,
} from "./api";
import { BulkEditColumnDialog } from "./BulkEditColumnDialog";
import { FacilityMatchDialog } from "./FacilityMatchDialog";
import { ManagePipelineOutputTagsDialog } from "./ManagePipelineOutputTags";
import {
  PIPELINE_REJECTED_FILTER_GROUPS,
  PIPELINE_REJECTED_FILTER_KEYS,
  PIPELINE_ROW_FILTER_GROUPS,
  PIPELINE_ROW_FILTER_KEYS,
} from "./pipelineRowFilters";
import { getIngestionAlertSeverity, getIngestionStatusColor } from "./pipelineUtils";

type ReviewRowBase = WastePipelineRunRowsQuery["wastePipelineRunRows"]["items"][number];
type RejectedRowBase =
  WastePipelineRunRejectedRowsQuery["wastePipelineRunRejectedRows"]["items"][number];
type ReviewRow = ReviewRowBase;
type RejectedRow = RejectedRowBase;
type RejectedRowGroup =
  WastePipelineRunRejectedRowGroupsQuery["wastePipelineRunRejectedRowGroups"]["items"][number];
type RejectedRowByReason =
  WastePipelineRunRejectedRowsByReasonQuery["wastePipelineRunRejectedRowsByReason"]["items"][number];

const REJECTED_ROW_REFETCH_QUERIES = [
  "WastePipelineRunRejectedRowGroups",
  "WastePipelineRunMetadata",
  // Includes the expanded detail-panel rows so a stale "Unresolved" chip
  // on individual rows doesn't outlive the group-level refresh.
  "WastePipelineRunRejectedRowsByReason",
];

// Tab positions are referenced in several places (default-tab effect, the
// per-tab render blocks below, and `setActiveTab` calls). Centralizing them
// here so the next tab insertion is a one-line shift.
const PipelineResultsTab = {
  Matched: 0,
  Rejected: 1,
  Notices: 2,
  OutputFiles: 3,
} as const;
type PipelineResultsTab = (typeof PipelineResultsTab)[keyof typeof PipelineResultsTab];

export const PipelineRunResults = () => {
  const { pipelineId } = useParams<{ pipelineId: string }>();
  const alerts = useAlerts();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<PipelineResultsTab>(PipelineResultsTab.Matched);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [ingestResult, setIngestResult] = useState<{ jobId: string } | null>(null);
  const [ingestError, setIngestError] = useState<string | null>(null);
  const [facilityDialogGroup, setFacilityDialogGroup] = useState<RejectedRowGroup | null>(null);
  // Tracks the Rejected tab's effective omnisearch string so the drill-in
  // detail panel can inherit it as a `defaultSearch` baseline.
  const [rejectedSearch, setRejectedSearch] = useState<string | undefined>(undefined);
  // OmnisearchDataGrid tracks selection internally (including cross-page); we
  // mirror the resolved rows here so the bulk-actions menu can act on them,
  // and hold the IDs in controlled state so dialogs can clear the selection.
  const [selectedRows, setSelectedRows] = useState<ReviewRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<GridRowId[]>([]);
  const handleSelectedRowsChanged = useCallback((rows: ReviewRow[]) => {
    setSelectedRows(rows);
  }, []);
  const handleSelectedIdsChanged = useCallback((ids: GridRowId[]) => {
    setSelectedIds(ids);
  }, []);
  const clearSelection = useCallback(() => {
    setSelectedIds([]);
    setSelectedRows([]);
  }, []);
  const [manageTagsRows, setManageTagsRows] = useState<
    | {
        id: string;
        tags: string[];
        reportable?: string | null;
        overrideManualEdits?: string | null;
      }[]
    | null
  >(null);
  const [bulkActionsAnchor, setBulkActionsAnchor] = useState<HTMLElement | null>(null);
  const [bulkEditColumn, setBulkEditColumn] = useState<{
    fieldName: string;
    fieldLabel: string;
    options: { value: string; label: string }[];
    rows: {
      id: string;
      currentValue?: string | null;
      tags?: string[] | null;
      reportable?: string | null;
      overrideManualEdits?: string | null;
    }[];
  } | null>(null);

  // Metadata + pipeline info
  const {
    data: metadataData,
    loading: metadataLoading,
    refetch: refetchMetadata,
  } = useQuery(WASTE_PIPELINE_RUN_METADATA_QUERY, {
    variables: { pipelineId: pipelineId ?? "" },
    skip: !pipelineId,
  });

  const metadata = metadataData?.wastePipelineRunMetadata;
  const pipeline = metadataData?.pipeline;

  // Default to Output Files tab when there are no matched / rejected / notification rows.
  const hasSetDefaultTab = useRef(false);
  useEffect(() => {
    if (hasSetDefaultTab.current || metadataLoading) return;
    const matched = metadata?.matchedRows ?? 0;
    const rejected = metadata?.rejectedRowCount ?? 0;
    const notifications = metadata?.notificationRowCount ?? 0;
    if (matched === 0 && rejected === 0 && notifications === 0) {
      setActiveTab(PipelineResultsTab.OutputFiles);
    }
    hasSetDefaultTab.current = true;
  }, [metadata, metadataLoading]);

  // Per-tab pagination state — kept independent so switching tabs preserves
  // position. The Matched and Rejected tabs manage their own page/sort/search
  // inside OmnisearchDataGrid (via their `persistenceKey`) so they aren't
  // tracked here. Notifications still uses the legacy DataGrid.
  const [notificationsPaginationModel, setNotificationsPaginationModel] = usePaginationModel(100);

  // Notifications use the same rejected-groups query shape but a different
  // rejectionType filter. The Rejected tab is wired through OmnisearchDataGrid
  // directly (see the JSX below) and owns its own query lifecycle.
  const notificationRowGroupsVariables = useMemo(
    () => ({
      pipelineId: pipelineId ?? "",
      rejectionType: PipelineRejectionType.Notification,
      page: notificationsPaginationModel.page,
      pageSize: notificationsPaginationModel.pageSize,
    }),
    [pipelineId, notificationsPaginationModel.page, notificationsPaginationModel.pageSize]
  );
  const {
    data: notificationGroupsData,
    previousData: notificationGroupsPreviousData,
    loading: notificationsLoading,
  } = useQuery(WASTE_PIPELINE_RUN_REJECTED_ROW_GROUPS_QUERY, {
    variables: notificationRowGroupsVariables,
    skip: !pipelineId || activeTab !== PipelineResultsTab.Notices,
  });

  const [ingest, { loading: ingestLoading }] = useMutation(INGEST_WASTE_PIPELINE_DATA_MUTATION);
  const [updatePipelineOutput] = useMutation(UPDATE_PIPELINE_OUTPUT_DATA_MUTATION);
  const [reprocessResolved, { loading: reprocessLoading }] = useMutation(
    REPROCESS_RESOLVED_PIPELINE_ROWS_MUTATION
  );
  const [markRowsResolved] = useMutation(MARK_FACILITY_NOT_FOUND_ROWS_RESOLVED_MUTATION);
  const [unmarkRowsResolved] = useMutation(UNMARK_FACILITY_NOT_FOUND_ROWS_RESOLVED_MUTATION);
  const [resolutionPendingReasons, setResolutionPendingReasons] = useState<Set<string>>(
    () => new Set()
  );

  const handleToggleResolved = useCallback(
    async (group: RejectedRowGroup) => {
      if (!pipelineId) return;
      const isResolved = !!group.resolvedAt;
      setResolutionPendingReasons((prev) => {
        const next = new Set(prev);
        next.add(group.rejectionReason);
        return next;
      });
      try {
        if (isResolved) {
          const result = await unmarkRowsResolved({
            variables: { pipelineId, rejectionReason: group.rejectionReason },
            refetchQueries: REJECTED_ROW_REFETCH_QUERIES,
            awaitRefetchQueries: true,
          });
          const count = result.data?.unmarkFacilityNotFoundRowsResolved ?? 0;
          alerts.success(`${count} row${count === 1 ? "" : "s"} marked unresolved.`);
        } else {
          const result = await markRowsResolved({
            variables: {
              pipelineId,
              rejectionReason: group.rejectionReason,
              resolvedFacilityId: null,
            },
            refetchQueries: REJECTED_ROW_REFETCH_QUERIES,
            awaitRefetchQueries: true,
          });
          const count = result.data?.markFacilityNotFoundRowsResolved ?? 0;
          alerts.success(`${count} row${count === 1 ? "" : "s"} marked resolved.`);
        }
      } catch (err) {
        alerts.error(
          `Failed to update resolution: ${err instanceof Error ? err.message : "unknown error"}`
        );
      } finally {
        setResolutionPendingReasons((prev) => {
          const next = new Set(prev);
          next.delete(group.rejectionReason);
          return next;
        });
      }
    },
    [pipelineId, markRowsResolved, unmarkRowsResolved, alerts]
  );

  const handleReprocessResolved = useCallback(async () => {
    if (!pipelineId) return;
    try {
      const result = await reprocessResolved({
        variables: { pipelineId },
        refetchQueries: REJECTED_ROW_REFETCH_QUERIES,
        awaitRefetchQueries: true,
      });
      const jobId = result.data?.reprocessResolvedPipelineRows?.id;
      if (jobId) {
        alerts.success(`Reprocess job dispatched (id: ${jobId}).`);
      } else {
        alerts.success("Reprocess job dispatched.");
      }
    } catch (err) {
      alerts.error(
        `Failed to dispatch reprocess: ${err instanceof Error ? err.message : "unknown error"}`
      );
    }
  }, [pipelineId, reprocessResolved, alerts]);

  const handleTagsCellDoubleClick = useCallback(
    (params: GridCellParams<ReviewRow>) => {
      if (params.field === "tags") {
        const row = params.row;
        setManageTagsRows([
          {
            id: row.id,
            tags: row.tags ?? [],
            reportable: row.reportable,
            overrideManualEdits: row.overrideManualEdits,
          },
        ]);
      }
    },
    [setManageTagsRows]
  );

  const processRowUpdate = useCallback(
    async (newRow: ReviewRow, oldRow: ReviewRow) => {
      const norm = (v: unknown) => (typeof v === "string" && v.trim() === "" ? null : v);
      const changedFields: Record<string, unknown> = {};
      for (const key of Object.keys(newRow) as (keyof ReviewRow)[]) {
        if (key === "id" || key === "__typename") continue;
        if (norm(newRow[key]) !== norm(oldRow[key])) changedFields[key] = newRow[key];
      }
      if (Object.keys(changedFields).length > 0) {
        try {
          await updatePipelineOutput({
            variables: { id: newRow.id, input: changedFields },
          });
          alerts.success("Saved", { autoHideDuration: 2000 });
        } catch (err) {
          alerts.error(`Failed to save: ${err instanceof Error ? err.message : "unknown error"}`);
          throw err;
        }
      }
      return newRow;
    },
    [updatePipelineOutput, alerts]
  );

  const handleIngestConfirm = useCallback(async () => {
    if (!pipelineId) return;
    try {
      const result = await ingest({
        variables: { pipelineId },
      });
      const jobId = result.data?.ingestWastePipelineData?.id;
      if (jobId) {
        setIngestResult({ jobId });
      }
      setIngestError(null);
      setConfirmOpen(false);
      refetchMetadata();
    } catch (err) {
      setIngestError(err instanceof Error ? err.message : "Ingestion failed");
      setConfirmOpen(false);
    }
  }, [pipelineId, ingest, refetchMetadata]);

  const ingestionJobInProgress =
    !!pipeline?.ingestionJob?.status &&
    inProgressJobStatuses.includes(pipeline.ingestionJob.status);

  const pipelineName = pipeline?.name ?? "Pipeline";

  useBreadcrumb([
    { label: "Pipelines", to: "/staff/pipelines" },
    { label: pipelineName },
    { label: "Results" },
  ]);

  const reviewColumns = useReviewColumns();
  const rejectedGroupColumns = useRejectedGroupColumns(
    setFacilityDialogGroup,
    handleToggleResolved,
    resolutionPendingReasons
  );
  const notificationGroupColumns = useNotificationGroupColumns();

  const notificationGroups = useMemo(
    () =>
      notificationGroupsData?.wastePipelineRunRejectedRowGroups.items ??
      notificationGroupsPreviousData?.wastePipelineRunRejectedRowGroups.items ??
      [],
    [
      notificationGroupsData?.wastePipelineRunRejectedRowGroups.items,
      notificationGroupsPreviousData?.wastePipelineRunRejectedRowGroups.items,
    ]
  );
  const notificationGroupCount =
    notificationGroupsData?.wastePipelineRunRejectedRowGroups.count ??
    notificationGroupsPreviousData?.wastePipelineRunRejectedRowGroups.count ??
    0;

  if (!pipelineId) {
    return <Typography>No pipeline ID provided.</Typography>;
  }

  if (metadataLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" py={8}>
        <CircularProgress />
      </Stack>
    );
  }

  const matchedRowCount = metadata?.matchedRows ?? 0;
  const rejectedRowCount = metadata?.rejectedRowCount ?? 0;
  const notificationRowCount = metadata?.notificationRowCount ?? 0;
  const pendingReprocessRowCount = metadata?.pendingReprocessRowCount ?? 0;
  const reprocessDisabledReason =
    pendingReprocessRowCount === 0
      ? "No rows are awaiting reprocess. Mark resolved rows from the Rejected Rows tab to enable this action."
      : null;

  return (
    <Stack
      spacing={3}
      py={3}
      px={2}
      sx={{
        height: {
          xs: `calc(100vh - ${theme.spacing(appBarXsHeight)})`,
          md: `calc(100vh - ${theme.spacing(appBarMdHeight)})`,
        },
        minHeight: 0,
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2}>
        <Button component={Link} to="/staff/pipelines" startIcon={<ArrowBack />}>
          Back to Pipelines
        </Button>
        <Typography variant="h5">{pipelineName} — Results</Typography>
        {pipeline?.tenant?.name && (
          <Typography variant="subtitle1" color="text.secondary">
            {pipeline.tenant.name}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip
          title={reprocessDisabledReason ?? ""}
          disableHoverListener={!reprocessDisabledReason}
        >
          <span>
            <Button
              variant="outlined"
              startIcon={<Replay />}
              onClick={handleReprocessResolved}
              disabled={reprocessLoading || pendingReprocessRowCount === 0}
            >
              Reprocess rows
              {pendingReprocessRowCount > 0 ? ` (${pendingReprocessRowCount})` : ""}
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="contained"
          startIcon={<PublishIcon />}
          onClick={() => setConfirmOpen(true)}
          disabled={ingestLoading || ingestionJobInProgress || matchedRowCount === 0}
        >
          Ingest Pipeline
        </Button>
      </Stack>

      {ingestResult && (
        <Alert severity="success" onClose={() => setIngestResult(null)}>
          Ingestion job created:{" "}
          <Link to={`/staff/jobs/${ingestResult.jobId}`}>{ingestResult.jobId}</Link>
        </Alert>
      )}
      {ingestError && (
        <Alert severity="error" onClose={() => setIngestError(null)}>
          {ingestError}
        </Alert>
      )}

      {pipeline?.ingestionJob && (
        <IngestionStatusBanner
          jobId={pipeline.ingestionJob.id}
          status={pipeline.ingestionJob.status}
          ingestedAt={pipeline.ingestedAt}
        />
      )}

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleIngestConfirm}
        loading={ingestLoading}
        header="Ingest Pipeline Data"
        info={
          pipeline?.ingestedAt
            ? `This pipeline was previously ingested on ${prettyPrintDateTime(pipeline.ingestedAt)}. Re-ingesting will overwrite existing data.`
            : `This will import ${matchedRowCount.toLocaleString()} matched rows into the system for ${pipeline?.tenant?.name ?? "this tenant"}.`
        }
        confirmText="Ingest"
        confirmStartIcon={<PublishIcon />}
      />

      {/* Summary Stats (only shown when lastRunSummary exists) */}
      {metadata && <MetadataSummary metadata={metadata} />}

      {/* Tabs: Matched Rows / Rejected Rows / Data Quality Notices / Output Files */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <Stack direction="row" alignItems="center" sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue: PipelineResultsTab) => setActiveTab(newValue)}
            sx={{ flexGrow: 1 }}
          >
            <Tab label={`Matched Rows (${matchedRowCount.toLocaleString()})`} />
            <Tab label={`Rejected Rows (${rejectedRowCount.toLocaleString()})`} />
            <Tab label={`Data Quality Notices (${notificationRowCount.toLocaleString()})`} />
            <Tab label="Output Files" />
          </Tabs>
          {activeTab === PipelineResultsTab.Matched && (
            <>
              <Button
                endIcon={<ArrowDropDown />}
                onClick={(e) => setBulkActionsAnchor(e.currentTarget)}
                disabled={selectedRows.length === 0}
                size="small"
                variant="outlined"
              >
                Bulk Actions ({selectedRows.length})
              </Button>
              <Menu
                anchorEl={bulkActionsAnchor}
                open={!!bulkActionsAnchor}
                onClose={() => setBulkActionsAnchor(null)}
              >
                <MenuItem
                  onClick={() => {
                    setBulkActionsAnchor(null);
                    setManageTagsRows(
                      selectedRows.map((row) => ({
                        id: row.id,
                        tags: row.tags ?? [],
                        reportable: row.reportable,
                        overrideManualEdits: row.overrideManualEdits,
                      }))
                    );
                  }}
                >
                  <ListItemIcon>
                    <Label fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Manage Tags</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setBulkActionsAnchor(null);
                    setBulkEditColumn({
                      fieldName: "reportable",
                      fieldLabel: "Reportable",
                      options: [
                        { value: "Reportable", label: "Reportable" },
                        { value: "Not Reportable", label: "Not Reportable" },
                      ],
                      rows: selectedRows.map((row) => ({
                        id: row.id,
                        currentValue: row.reportable,
                        tags: row.tags,
                        reportable: row.reportable,
                        overrideManualEdits: row.overrideManualEdits,
                      })),
                    });
                  }}
                >
                  <ListItemIcon>
                    <EditNote fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Set Reportable</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setBulkActionsAnchor(null);
                    setBulkEditColumn({
                      fieldName: "overrideManualEdits",
                      fieldLabel: "Override Manual Edits",
                      options: [
                        { value: "Y", label: "Yes" },
                        { value: "N", label: "No" },
                      ],
                      rows: selectedRows.map((row) => ({
                        id: row.id,
                        currentValue: row.overrideManualEdits,
                        tags: row.tags,
                        reportable: row.reportable,
                        overrideManualEdits: row.overrideManualEdits,
                      })),
                    });
                  }}
                >
                  <ListItemIcon>
                    <EditNote fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Set Override Manual Edits</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </Stack>

        {activeTab === PipelineResultsTab.Matched && (
          <>
            <Box sx={{ mt: 2, flex: 1, minHeight: 0 }}>
              <OmnisearchDataGrid<
                WastePipelineRunRowsQuery,
                WastePipelineRunRowsQueryVariables,
                ReviewRow
              >
                fillParentHeight
                hideFooter={false}
                persistenceKey={`pipeline-matched-rows-${pipelineId}`}
                isURLDriven
                initialPageSize={100}
                pageSizeOptions={[50, 100, 250, 500]}
                dataQuery={WASTE_PIPELINE_RUN_ROWS_QUERY}
                dataQueryVariables={{ pipelineId }}
                getItems={(data) => data?.wastePipelineRunRows?.items ?? []}
                getCount={(data) => data?.wastePipelineRunRows?.count ?? 0}
                columns={reviewColumns}
                filterGroups={PIPELINE_ROW_FILTER_GROUPS}
                additionalFilterColumns={PIPELINE_ROW_FILTER_KEYS}
                excludeFilterColumns={reviewColumns.map((col) => col.field)}
                getRowId={(row) => row.id}
                noDataMessage="No matched rows found"
                selectedIds={selectedIds}
                onSelectedIdsChanged={handleSelectedIdsChanged}
                onSelectedRowsChanged={handleSelectedRowsChanged}
                disableSelectAll
                processRowUpdate={processRowUpdate}
                onCellDoubleClick={handleTagsCellDoubleClick}
              />
            </Box>
            {manageTagsRows && pipeline?.tenantId && (
              <ManagePipelineOutputTagsDialog
                selectedRows={manageTagsRows}
                tenantId={pipeline.tenantId}
                dialogOpen={!!manageTagsRows}
                onClose={() => {
                  setManageTagsRows(null);
                  clearSelection();
                }}
              />
            )}
            {bulkEditColumn && (
              <BulkEditColumnDialog
                open
                onClose={() => {
                  setBulkEditColumn(null);
                  clearSelection();
                }}
                fieldName={bulkEditColumn.fieldName}
                fieldLabel={bulkEditColumn.fieldLabel}
                options={bulkEditColumn.options}
                selectedRows={bulkEditColumn.rows}
              />
            )}
          </>
        )}

        {activeTab === PipelineResultsTab.Rejected && pipelineId && (
          <Stack spacing={2} sx={{ mt: 2, flex: 1, minHeight: 0 }}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <OmnisearchDataGrid<
                WastePipelineRunRejectedRowGroupsQuery,
                WastePipelineRunRejectedRowGroupsQueryVariables,
                RejectedRowGroup
              >
                fillParentHeight
                hideFooter={false}
                persistenceKey={`pipeline-rejected-row-groups-${pipelineId}`}
                isURLDriven
                dataQuery={WASTE_PIPELINE_RUN_REJECTED_ROW_GROUPS_QUERY}
                dataQueryVariables={{
                  pipelineId,
                  rejectionType: PipelineRejectionType.Rejection,
                }}
                getItems={(d) => d?.wastePipelineRunRejectedRowGroups?.items ?? []}
                getCount={(d) => d?.wastePipelineRunRejectedRowGroups?.count ?? 0}
                columns={rejectedGroupColumns}
                filterGroups={PIPELINE_REJECTED_FILTER_GROUPS}
                additionalFilterColumns={PIPELINE_REJECTED_FILTER_KEYS}
                excludeFilterColumns={rejectedGroupColumns.map((col) => col.field)}
                initialPageSize={100}
                pageSizeOptions={[25, 50, 100]}
                getRowId={(row) => row.rejectionReason}
                onSearchChanged={setRejectedSearch}
                getDetailPanelContent={({ row }) => (
                  <RejectedRowGroupChildren
                    pipelineId={pipelineId}
                    rejectionReason={row.rejectionReason}
                    rejectionType={PipelineRejectionType.Rejection}
                    parentOmnisearch={rejectedSearch}
                  />
                )}
                getDetailPanelHeight={() => "auto"}
                disableRowSelectionOnClick
                noDataMessage="No rejected rows found"
              />
            </Box>
          </Stack>
        )}

        {activeTab === PipelineResultsTab.Notices && pipelineId && (
          <Stack spacing={2} sx={{ mt: 2, flex: 1, minHeight: 0 }}>
            <Typography variant="body2" color="text.secondary">
              Informational findings flagged during this run. These rows were imported normally —
              the entries below are QA/QC notices (e.g. unrecognized vendor descriptions) for human
              review and do not block ingest.
            </Typography>
            <Box
              sx={{
                position: "relative",
                flex: 1,
                minHeight: 0,
              }}
            >
              {notificationsLoading && notificationGroupsPreviousData && (
                <LinearProgress
                  sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 2 }}
                />
              )}
              <DataGrid
                rows={notificationGroups}
                columns={notificationGroupColumns}
                loading={notificationsLoading && !notificationGroupsPreviousData}
                sx={{
                  opacity:
                    notificationsLoading && notificationGroupsPreviousData
                      ? theme.palette.action.disabledOpacity
                      : 1,
                }}
                paginationMode="server"
                rowCount={notificationGroupCount}
                paginationModel={notificationsPaginationModel}
                onPaginationModelChange={setNotificationsPaginationModel}
                pageSizeOptions={[25, 50, 100]}
                getRowId={(row) => row.rejectionReason}
                getDetailPanelContent={({ row }: { row: RejectedRowGroup }) => (
                  <RejectedRowGroupChildren
                    pipelineId={pipelineId}
                    rejectionReason={row.rejectionReason}
                    rejectionType={PipelineRejectionType.Notification}
                  />
                )}
                getDetailPanelHeight={() => "auto"}
                disableRowSelectionOnClick
                noRowsMessage="No data quality notices found"
              />
            </Box>
          </Stack>
        )}

        {activeTab === PipelineResultsTab.OutputFiles && (
          <Box sx={{ mt: 2 }}>
            {pipeline && <PipelineDetailsSummary pipeline={pipeline} />}

            <Typography variant="subtitle1" fontWeight="medium" sx={{ mt: 2, mb: 1 }}>
              Output Files ({pipeline?.outputFiles?.length ?? 0})
            </Typography>
            {pipeline?.outputFiles && pipeline.outputFiles.length > 0 ? (
              <Stack spacing={1}>
                {pipeline.outputFiles.map((fileKey) => (
                  <S3FileLink key={fileKey} fileKey={fileKey} />
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary">No output files available.</Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Facility match dialog */}
      {facilityDialogGroup && pipeline?.tenantId && pipelineId && (
        <FacilityMatchDialog
          open
          onClose={() => setFacilityDialogGroup(null)}
          extractedAddress={facilityDialogGroup.sampleExtractedAddress ?? null}
          tenantId={pipeline.tenantId}
          pipelineId={pipelineId}
          rejectionReason={facilityDialogGroup.rejectionReason}
        />
      )}
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Metadata Summary — single compact card
// ---------------------------------------------------------------------------

type MetadataType = NonNullable<WastePipelineRunMetadataQuery["wastePipelineRunMetadata"]>;

function MetadataSummary({ metadata }: { metadata: MetadataType }) {
  const countStats: StatItem[] = [
    { label: "Source", value: metadata.sourceRows },
    { label: "Matched", value: metadata.matchedRows },
    { label: "Rejected", value: metadata.rejectedRowCount },
    { label: "Notices", value: metadata.notificationRowCount },
    { label: "Manifests", value: metadata.uniqueManifests },
    { label: "Facilities", value: metadata.uniqueFacilities },
  ];

  const classificationStats: StatItem[] = [
    { label: "RCRA Haz", value: metadata.classificationRcraHazardous },
    { label: "Acute Haz", value: metadata.classificationRcraAcutelyHazardous },
    { label: "State Reg", value: metadata.classificationStateRegulated },
    { label: "Nonhaz", value: metadata.classificationNonhazardous },
  ];

  const reportabilityStats: StatItem[] = [
    { label: "Reportable", value: metadata.reportable },
    { label: "Not Reportable", value: metadata.notReportable },
  ];

  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Stack
          direction="row"
          spacing={2}
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
          divider={<Divider orientation="vertical" flexItem />}
          sx={{ mb: 1 }}
        >
          <Typography variant="caption" color="text.secondary">
            Vendor: <strong>{metadata.vendorSource}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Run Key: <strong>{metadata.runKey}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Generated:{" "}
            <strong>
              {metadata.generatedAt ? prettyPrintDateTime(metadata.generatedAt) : "—"}
            </strong>
          </Typography>
        </Stack>

        <Divider sx={{ mb: 1 }} />

        <Stack
          direction="row"
          spacing={2}
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
          divider={<Divider orientation="vertical" flexItem />}
        >
          <StatGroup stats={countStats} />
          <StatGroup stats={classificationStats} />
          <StatGroup stats={reportabilityStats} />
        </Stack>
      </CardContent>
    </Card>
  );
}

type StatItem = { label: string; value: number | null | undefined };

function StatGroup({ stats }: { stats: StatItem[] }) {
  return (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
      {stats.map((stat) => (
        <Typography key={stat.label} variant="body2" color="text.secondary">
          {stat.label}: <strong>{stat.value?.toLocaleString() ?? "—"}</strong>
        </Typography>
      ))}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Pipeline details — mirrors info from the old PipelineDialog
// ---------------------------------------------------------------------------

type PipelineType = NonNullable<WastePipelineRunMetadataQuery["pipeline"]>;

function PipelineDetailsSummary({ pipeline }: { pipeline: PipelineType }) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
        <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: "block" }}>
          Pipeline Details
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Pipeline ID
            </Typography>
            <CopyableTypography variant="body2" text={pipeline.id} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Name
            </Typography>
            <Typography variant="body2">{pipeline.name}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Tenant
            </Typography>
            <Typography variant="body2">{pipeline.tenant?.name ?? "—"}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Last Run
            </Typography>
            <Typography variant="body2">
              {pipeline.lastRunAt ? prettyPrintDateTime(pipeline.lastRunAt) : "Never"}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Created
            </Typography>
            <Typography variant="body2">{prettyPrintDateTime(pipeline.createdAt)}</Typography>
            <Typography variant="body2">{pipeline.createdBy}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Updated
            </Typography>
            <Typography variant="body2">{prettyPrintDateTime(pipeline.updatedAt)}</Typography>
            <Typography variant="body2">{pipeline.updatedBy}</Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Input Files ({pipeline.inputFiles?.length ?? 0})
            </Typography>
            {pipeline.inputFiles && pipeline.inputFiles.length > 0 ? (
              <Box>
                {pipeline.inputFiles.map((file) => (
                  <S3FileLink key={file} fileKey={file} />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No input files
              </Typography>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Ingestion status banner
// ---------------------------------------------------------------------------

function IngestionStatusBanner({
  jobId,
  status,
  ingestedAt,
}: {
  jobId: string;
  status: string;
  ingestedAt?: string | null;
}) {
  const severity = getIngestionAlertSeverity(status);

  return (
    <Alert severity={severity} icon={false} sx={{ alignItems: "center" }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="body2">
          <strong>Ingestion:</strong>
        </Typography>
        <Chip size="small" label={status} color={getIngestionStatusColor(status)} />
        <Typography variant="body2" color="text.secondary">
          Job: <Link to={`/staff/jobs/${jobId}`}>{jobId}</Link>
        </Typography>
        {ingestedAt && status === "SUCCEEDED" && (
          <Typography variant="body2" color="text.secondary">
            Completed: {prettyPrintDateTime(ingestedAt)}
          </Typography>
        )}
      </Stack>
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// Rejected row detail panel (shows rawData JSON)
// ---------------------------------------------------------------------------

function getDetailPanelContent({ row }: { row: RejectedRow }) {
  if (!row.rawData) return null;
  return (
    <Box sx={{ p: 2, bgcolor: "grey.50" }}>
      <Typography variant="subtitle2" gutterBottom>
        Original Row Data
      </Typography>
      <Box
        component="pre"
        sx={{
          fontSize: "0.75rem",
          maxHeight: 400,
          overflow: "auto",
          bgcolor: "grey.100",
          p: 1.5,
          borderRadius: 1,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {JSON.stringify(row.rawData, null, 2)}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

function useReviewColumns(): OmnisearchGridColDef<ReviewRow>[] {
  return useMemo(
    () => [
      { field: "manifestTrackingNumber", headerName: "Manifest Tracking Number", editable: true },
      {
        field: "tags",
        headerName: "Waste Line Tags",
        minWidth: 200,
        renderCell: ({ value }: GridRenderCellParams) => {
          const tags = value as string[] | null;
          if (!tags || tags.length === 0) return null;
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
              {tags.map((tag) => (
                <Chip key={tag} label={tag} size="small" variant="outlined" />
              ))}
            </Box>
          );
        },
      },
      {
        field: "reportable",
        headerName: "Reportable",
        editable: true,
        type: "singleSelect",
        valueOptions: ["Reportable", "Not Reportable"],
        minWidth: 150,
        renderCell: ({ value }: GridRenderCellParams) => {
          if (!value) return null;
          return (
            <Chip
              label={value}
              size="small"
              variant="outlined"
              color={value === "Reportable" ? "success" : "default"}
              sx={{ pointerEvents: "none" }}
            />
          );
        },
      },
      {
        field: "overrideManualEdits",
        headerName: "Override Manual Edits",
        editable: true,
        type: "singleSelect",
        valueOptions: [
          { value: "Y", label: "Yes" },
          { value: "N", label: "No" },
        ],
        minWidth: 170,
        renderCell: ({ value }: GridRenderCellParams) => {
          if (!value) return null;
          const isYes = value === "Y";
          return (
            <Chip
              label={isYes ? "Yes" : "No"}
              size="small"
              variant="outlined"
              color={isYes ? "warning" : "default"}
              sx={{ pointerEvents: "none" }}
            />
          );
        },
      },
      { field: "shippedDate", headerName: "Shipped Date", editable: true },
      { field: "receivedDate", headerName: "Received Date", editable: true },
      {
        field: "facilityId",
        headerName: "Encamp Facility ID",
        editable: true,
        renderCell: ({ row, value }: GridRenderCellParams<ReviewRow>) => {
          if (!value) return null;
          const method = row.facilityMatchMethod;
          if (!method) return value;
          return (
            <Tooltip title={`Match method: ${method}`} arrow>
              <span>{value}</span>
            </Tooltip>
          );
        },
      },
      { field: "generatorId", headerName: "Generator EPA ID", editable: true },
      { field: "generatorName", headerName: "Generator Name", editable: true },
      {
        field: "generatorLocationStreetNo",
        headerName: "Generator Location Street Number",
        editable: true,
      },
      {
        field: "generatorLocationStreet1",
        headerName: "Generator Location Street 1",
        editable: true,
      },
      {
        field: "generatorLocationStreet2",
        headerName: "Generator Location Street 2",
        editable: true,
      },
      { field: "generatorLocationCity", headerName: "Generator Location City", editable: true },
      { field: "generatorLocationZip", headerName: "Generator Location Zip", editable: true },
      { field: "generatorLocationState", headerName: "Generator Location State", editable: true },
      { field: "desFacilityId", headerName: "Designated Facility EPA ID", editable: true },
      { field: "desFacilityName", headerName: "Designated Facility Name", editable: true },
      { field: "vendorSource", headerName: "Vendor Source", editable: true },
      { field: "vendorSiteId", headerName: "Vendor Site ID", editable: true },
      { field: "wasteLineNumber", headerName: "Waste Line Number", editable: true },
      { field: "dotHazardous", headerName: "U.S. DOT Hazardous Indicator", editable: true },
      { field: "dotIdNumberDescription", headerName: "U.S. DOT Description", editable: true },
      { field: "dotPrintedInformation", headerName: "DOT Printed Information", editable: true },
      {
        field: "nonHazWasteDescription",
        headerName: "Non-Hazardous Waste Description",
        editable: true,
      },
      {
        field: "containerNumber",
        headerName: "Number of Containers",
        type: "number",
        editable: true,
      },
      { field: "containerTypeCode", headerName: "Container Type Code", editable: true },
      { field: "quantity", headerName: "Waste Quantity", editable: true },
      {
        field: "qtyUnitOfMeasureCode",
        headerName: "Quantity Unit of Measure Code",
        editable: true,
      },
      { field: "managementMethodCode", headerName: "Management Method Code", editable: true },
      {
        field: "managementMethodDescription",
        headerName: "Management Method Description",
        editable: true,
      },
      {
        field: "finalManagementMethodCode",
        headerName: "Final Management Method Code",
        editable: true,
      },
      {
        field: "finalManagementMethodDescription",
        headerName: "Final Management Method Description",
        editable: true,
      },
      { field: "brDensity", headerName: "Waste Density", editable: true },
      {
        field: "brDensityUomCode",
        headerName: "Waste Density Unit of Measure Code",
        editable: true,
      },
      {
        field: "brDensityUomDescription",
        headerName: "Waste Density Unit of Measure Description",
        editable: true,
      },
      { field: "brFormCode", headerName: "Form Code", editable: true },
      { field: "brSourceCode", headerName: "Source Code", editable: true },
      { field: "epaWaste", headerName: "EPA Waste Indicator", editable: true },
      { field: "federalWasteCodes", headerName: "Federal Waste Codes", editable: true },
      { field: "stateWasteCodes", headerName: "State Waste Codes", editable: true },
      { field: "classification", headerName: "Classification", editable: true },
      { field: "vendorProfileNumber", headerName: "Vendor Profile Number", editable: true },
      {
        field: "vendorProfileDescription",
        headerName: "Vendor Profile Description",
        editable: true,
      },
      { field: "vendorComments", headerName: "Vendor Comments", editable: true },
      { field: "transporter1Id", headerName: "Transporter 1 EPA ID", editable: true },
      { field: "transporter1Name", headerName: "Transporter 1 Name", editable: true },
      {
        field: "transporter1DaysInPossession",
        headerName: "Transporter 1 Days In Possession",
        type: "number",
        editable: true,
      },
      { field: "transporter2Id", headerName: "Transporter 2 EPA ID", editable: true },
      { field: "transporter2Name", headerName: "Transporter 2 Name", editable: true },
      {
        field: "transporter2DaysInPossession",
        headerName: "Transporter 2 Days In Possession",
        type: "number",
        editable: true,
      },
      { field: "transporter3Id", headerName: "Transporter 3 EPA ID", editable: true },
      { field: "transporter3Name", headerName: "Transporter 3 Name", editable: true },
      {
        field: "transporter3DaysInPossession",
        headerName: "Transporter 3 Days In Possession",
        type: "number",
        editable: true,
      },
      { field: "transporter4Id", headerName: "Transporter 4 EPA ID", editable: true },
      { field: "transporter4Name", headerName: "Transporter 4 Name", editable: true },
      {
        field: "transporter4DaysInPossession",
        headerName: "Transporter 4 Days In Possession",
        type: "number",
        editable: true,
      },
    ],
    []
  );
}

function formatExtractedAddress(addr: RejectedRow["extractedAddress"]): string {
  if (!addr) return "—";
  const parts: string[] = [];
  if (addr.name) parts.push(addr.name);

  const location: string[] = [];
  if (addr.streetAddress) location.push(addr.streetAddress);
  if (addr.city) location.push(addr.city);
  if (addr.state && addr.zip) {
    location.push(`${addr.state} ${addr.zip}`);
  } else if (addr.state) {
    location.push(addr.state);
  } else if (addr.zip) {
    location.push(addr.zip);
  }

  if (location.length > 0) {
    parts.push(location.join(", "));
  }
  if (addr.epaId) parts.push(`EPA: ${addr.epaId}`);
  if (addr.vendorCode) parts.push(`Code: ${addr.vendorCode}`);

  return parts.join(" — ") || "—";
}

type GroupStatus = "unresolved" | "pendingReprocess" | "reprocessFailed";

function getGroupStatus(group: RejectedRowGroup): GroupStatus {
  if (!group.resolvedAt) return "unresolved";
  const resolvedAt = new Date(group.resolvedAt).getTime();
  const lastAttempt = group.lastReprocessAttemptAt
    ? new Date(group.lastReprocessAttemptAt).getTime()
    : 0;
  return lastAttempt >= resolvedAt ? "reprocessFailed" : "pendingReprocess";
}

function GroupStatusChip({ group }: { group: RejectedRowGroup }) {
  const status = getGroupStatus(group);
  if (status === "unresolved") {
    return <Chip size="small" variant="outlined" label="Unresolved" />;
  }
  if (status === "pendingReprocess") {
    return <Chip size="small" color="info" label="Pending reprocess" />;
  }
  return <Chip size="small" color="warning" label="Reprocess failed" />;
}

function useRejectedGroupColumns(
  onFindFacility: (row: RejectedRowGroup) => void,
  onToggleResolved: (row: RejectedRowGroup) => void,
  pendingReasons: Set<string>
): OmnisearchGridColDef<RejectedRowGroup>[] {
  return useMemo(
    () => [
      {
        field: "rejectionReason",
        headerName: "Rejection Reason",
        flex: 1,
        minWidth: 320,
      },
      {
        field: "rowCount",
        headerName: "Rows",
        width: 100,
        type: "number",
      },
      {
        field: "_status",
        headerName: "Status",
        width: 170,
        sortable: false,
        renderCell: (params: GridRenderCellParams<RejectedRowGroup>) => (
          <GroupStatusChip group={params.row} />
        ),
      },
      {
        field: "_actions",
        headerName: "",
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<RejectedRowGroup>) => {
          const isResolved = !!params.row.resolvedAt;
          const isLoading = pendingReasons.has(params.row.rejectionReason);
          return (
            <Stack direction="row" spacing={0.5}>
              <IconButton
                size="small"
                color="primary"
                aria-label="Find matching facility"
                onClick={() => onFindFacility(params.row)}
              >
                <DomainAdd fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="primary"
                disabled={isLoading}
                aria-label={isResolved ? "Remove from next reprocess" : "Queue for next reprocess"}
                onClick={() => onToggleResolved(params.row)}
              >
                {isLoading ? (
                  <CircularProgress size={16} />
                ) : isResolved ? (
                  <CheckBox fontSize="small" />
                ) : (
                  <CheckBoxOutlineBlank fontSize="small" />
                )}
              </IconButton>
            </Stack>
          );
        },
      },
    ],
    [onFindFacility, onToggleResolved, pendingReasons]
  );
}

function useNotificationGroupColumns(): GridColDef<RejectedRowGroup>[] {
  return useMemo(
    () => [
      {
        field: "rejectionReason",
        headerName: "Notice",
        flex: 1,
        minWidth: 320,
      },
      {
        field: "rowCount",
        headerName: "Rows",
        width: 100,
        type: "number",
      },
    ],
    []
  );
}

function RejectedRowGroupChildren({
  pipelineId,
  rejectionReason,
  rejectionType,
  parentOmnisearch,
}: {
  pipelineId: string;
  rejectionReason: string;
  rejectionType: PipelineRejectionType;
  /**
   * Optional baseline search inherited from the parent tab. The drill-in
   * snapshots this value on mount (see `baselineSearch` below) and forwards it
   * to OmnisearchDataDisplay's `defaultSearch` so the panel opens pre-filtered
   * to the parent context (e.g. expanding a group from a tab filtered by
   * `state:CA` keeps the rows scoped to California while still letting the user
   * refine inside). The snapshot prevents later parent-side edits from
   * rippling into already-open drill-in panels mid-typing.
   */
  parentOmnisearch?: string;
}) {
  // Snapshot the parent's omnisearch baseline at mount. The parent updates
  // `rejectedSearch` on every debounced keystroke, but we only want the
  // drill-in to inherit the value the parent had when this panel opened —
  // otherwise the merge in OmnisearchDataDisplay appends new parent tokens
  // into the drill-in while the user is typing here.
  const [baselineSearch] = useState(parentOmnisearch);

  const columns: OmnisearchGridColDef<RejectedRowByReason>[] = useMemo(
    () => [
      { field: "sourceFile", headerName: "Source File", width: 220 },
      { field: "sourceLineNumber", headerName: "Line #", width: 100, type: "number" },
      {
        field: "extractedAddress",
        headerName: "Address",
        flex: 1,
        minWidth: 260,
        sortable: false,
        renderCell: (params: GridRenderCellParams<RejectedRowByReason>) =>
          formatExtractedAddress(params.row.extractedAddress),
      },
    ],
    []
  );

  return (
    <Box sx={{ p: 2, bgcolor: "grey.50" }}>
      <Box>
        <OmnisearchDataGrid<
          WastePipelineRunRejectedRowsByReasonQuery,
          WastePipelineRunRejectedRowsByReasonQueryVariables,
          RejectedRowByReason
        >
          sx={{ height: 360 }}
          hideFooter={false}
          persistenceKey={`pipeline-rejected-drill-in-${pipelineId}-${rejectionReason.replace(
            /[^A-Za-z0-9_-]+/g,
            "_"
          )}`}
          isURLDriven={false}
          persistSortModel={false}
          defaultSearch={baselineSearch}
          dataQuery={WASTE_PIPELINE_RUN_REJECTED_ROWS_BY_REASON_QUERY}
          dataQueryVariables={{ pipelineId, rejectionReason, rejectionType }}
          getItems={(d) => d?.wastePipelineRunRejectedRowsByReason?.items ?? []}
          getCount={(d) => d?.wastePipelineRunRejectedRowsByReason?.count ?? 0}
          columns={columns}
          filterGroups={PIPELINE_REJECTED_FILTER_GROUPS}
          additionalFilterColumns={PIPELINE_REJECTED_FILTER_KEYS}
          excludeFilterColumns={columns.map((col) => col.field)}
          initialPageSize={50}
          pageSizeOptions={[25, 50, 100]}
          getRowId={(row) => row.id}
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={() => "auto"}
          disableRowSelectionOnClick
          noDataMessage="No rows in this group"
        />
      </Box>
    </Box>
  );
}
